import {
  Button,
  type ButtonProps as ButtonV3Props,
} from '@jouzen/ecom-components/v3/Button';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  Typography,
  type TypographyProps as TypographyV3Props,
} from '@jouzen/ecom-components/v3/Typography';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef, JSX, SVGProps } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '../Image';
import type { MotionProps } from '../Motion';
import Motion from '../Motion';

const twoColTextBlockVar = cva('flex flex-col justify-center', {
  variants: {
    smBreakTextPos: {
      top: null,
      bottom: null,
    },
    mdBreakTextPos: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-1 md:col-end-12 xxl:col-end-11',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-2 md:col-end-12 md:row-start-1 xxl:col-end-11',
    },
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-1 md:col-start-14',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-2 md:col-start-14 md:row-start-1',
    },
  ],
});

const twoColImageBlockVar = cva('flex flex-col justify-center', {
  variants: {
    smBreakTextPos: {
      top: null,
      bottom: null,
    },
    mdBreakTextPos: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'left',
      class:
        'col-main row-start-2 md:col-start-13 md:col-end-main md:row-start-1',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-1 md:col-start-13 md:col-end-main',
    },
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'right',
      class:
        'col-main row-start-2 md:col-start-main md:col-end-13 md:row-start-1',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-1 md:col-start-main md:col-end-13',
    },
  ],
});

type TwoColTextVarProps = VariantProps<typeof twoColTextBlockVar>;
type TwoColImageVarProps = VariantProps<typeof twoColImageBlockVar>;
type VarProps = TwoColTextVarProps & TwoColImageVarProps;

type Icon = (props: SVGProps<SVGSVGElement>) => JSX.Element;
export interface TypographyProps extends Omit<TypographyV3Props, 'children'> {
  readonly label: MessageKey;
}

export interface TextColProps {
  eyebrowProps?: TypographyProps;
  titleProps?: TypographyProps;
  buttonProps?: ButtonProps;
  paragraphs: {
    icon?: Icon;
    textProps: TypographyProps;
    subtextProps?: TypographyProps;
    iconClassName?: string;
  }[];
  textColClassName?: string;
  iconSize?: number;
  iconTextContainerClassName?: string;
}

export interface ImageColProps {
  localizedAlt: MessageKey;
  localizedSrc?: MessageKey;
  motionProps: MotionProps;
  src?: string;
}

export interface ButtonProps extends Omit<ButtonV3Props, 'children'> {
  readonly href?: string;
  readonly label: MessageKey;
  readonly siteName?: string;
}

export interface TwoColumnModuleProps extends VarProps {
  readonly imageCol: ImageColProps;
  readonly textCol: TextColProps;
  readonly layoutGridClassName?: string;
}

const TwoColumnModule = ({
  imageCol,
  smBreakTextPos = 'bottom',
  mdBreakTextPos = 'left',
  textCol,
  layoutGridClassName,
  ...props
}: TwoColumnModuleProps & ComponentPropsWithoutRef<'div'>) => {
  const t = useTranslations();
  const { label: eyebrowLabel, ...eyebrowProps } = textCol.eyebrowProps ?? {};
  const { label: titleLabel, ...titleProps } = textCol.titleProps ?? {};

  return (
    <div data-cy="two-column-module" {...props}>
      <LayoutGrid className={cx(layoutGridClassName ?? 'gap-y-12')}>
        <div
          className={twoColTextBlockVar({ smBreakTextPos, mdBreakTextPos })}
          data-cy="text-block"
        >
          {eyebrowLabel && (
            <Motion
              initial={{ opacity: 0 }}
              transition={{
                duration: 0.75,
                type: 'tween',
              }}
              viewport={{ amount: 0.33, once: true }}
              whileInView={{ opacity: 1 }}
            >
              <Typography
                Element="h2"
                {...eyebrowProps}
                data-cy="text-block-title"
              >
                {t(eyebrowLabel)}
              </Typography>
            </Motion>
          )}
          {titleLabel && (
            <Motion
              initial={{ opacity: 0 }}
              transition={{
                duration: 0.75,
                type: 'tween',
              }}
              viewport={{ amount: 0.33, once: true }}
              whileInView={{ opacity: 1 }}
            >
              <Typography
                Element="h2"
                {...titleProps}
                data-cy="text-block-title"
              >
                {t(titleLabel)}
              </Typography>
            </Motion>
          )}
          <div
            className={cx(textCol.textColClassName ?? 'flex flex-col gap-8')}
          >
            {textCol.paragraphs.map((paragraph, index) => {
              const { label: textLabel, ...textProps } = paragraph.textProps;
              const { label: subtextLabel, ...subtextProps } =
                paragraph.subtextProps ?? {};
              const ParaIcon = paragraph.icon;
              return (
                <Motion
                  key={textLabel}
                  className={cx(
                    textCol.iconTextContainerClassName ?? 'flex flex-row gap-4',
                  )}
                  initial={{ opacity: 0 }}
                  transition={{
                    delay: 0.1 * index,
                    duration: 0.75,
                    type: 'tween',
                  }}
                  viewport={{ amount: 0.66, once: true }}
                  whileInView={{ opacity: 1 }}
                  data-cy="icon-text-container"
                >
                  {ParaIcon && (
                    <div
                      data-cy="text-block-icon"
                      className={paragraph.iconClassName}
                    >
                      <ParaIcon
                        width={textCol.iconSize ?? 32}
                        height={textCol.iconSize ?? 32}
                      />
                    </div>
                  )}
                  <div data-cy="two-col-text-li">
                    <Typography {...textProps} Element="h3">
                      {t(textLabel)}
                    </Typography>
                    {subtextLabel && (
                      <Typography {...subtextProps}>
                        {t(subtextLabel)}
                      </Typography>
                    )}
                  </div>
                </Motion>
              );
            })}
            {textCol.buttonProps && (
              <Button {...textCol.buttonProps} data-cy="two-col-shop-cta">
                {t(textCol.buttonProps.label)}
              </Button>
            )}
          </div>
        </div>
        <div
          className={twoColImageBlockVar({ smBreakTextPos, mdBreakTextPos })}
          data-cy="image-block"
        >
          <Motion
            className="relative aspect-square overflow-hidden rounded-lg xl:aspect-5/4"
            {...imageCol.motionProps}
          >
            <Image
              className="rounded-lg object-cover"
              localizedAlt={imageCol.localizedAlt}
              localizedSrc={imageCol.localizedSrc}
              src={imageCol.src}
              fill
              sizes="(max-width: 1023px) 90vw, (max-width: 1567px) 50vw, 785px"
            />
          </Motion>
        </div>
      </LayoutGrid>
    </div>
  );
};

export default TwoColumnModule;
